import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { Counter } from "./Counter";
import { AmbientOrb } from "./Ambient";
import { WhatsAppIcon } from "./Floating";
import { HERO_IMAGE, PHONE, STATS, TRUST_STRIP, WHATSAPP_HREF } from "@/lib/data";

const headlineReveal = {
  hidden: { y: "100%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-[rgb(238,242,250)] pt-12 lg:pt-16" data-testid="hero-section">
      {/* Ambient decor */}
      <AmbientOrb className="-left-40 top-24" color="bg-gold-400/15" duration={12} />
      <AmbientOrb className="-right-24 top-56" color="bg-emerald2-400/10" size="h-96 w-96" duration={14} delay={1} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] grain-overlay" />
      {/* Grid lines */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(6,35,80,0.05)_1px,transparent_1px)] bg-[size:120px_100%]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-12 lg:px-8">
        {/* Left */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold-400/40 bg-gold-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-gold-700"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald2-500 opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald2-500" />
            </span>
            Expert-Led Business Compliance Platform
          </motion.div>

          <h1
            className="mt-6 overflow-hidden pb-1 font-bold tracking-tight text-navy-950 text-4xl leading-[1.15] sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            <motion.span variants={headlineReveal} initial="hidden" animate="show" className="block">
              Your <span className="text-[rgb(3,95,210)]">360°</span> Business Growth &amp;{" "}
              <span className="text-gold-500">Compliance Partner</span>
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 max-w-xl text-justify text-base leading-relaxed text-slate-600 md:text-lg"
          >
            From business registration to ongoing compliance, Biz360 Compliance delivers
            integrated business, taxation, accounting, payroll, licences and regulatory
            solutions through one trusted platform.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/contact#contact-form"
              data-testid="hero-cta-register"
              onClick={() => {
                try {
                  sessionStorage.setItem("scrollToContactForm", "true");
                } catch (_) {}
              }}
              className="group relative inline-flex items-center gap-2 rounded-full bg-gold-500 px-6 py-3.5 text-sm font-semibold text-navy-950 shadow-[0_12px_30px_rgba(212,162,76,0.4)] transition-transform duration-300 hover:scale-105"
            >
              <span className="absolute inset-0 -z-10 rounded-full bg-gold-500 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-60" />
              Register Your Business
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-cta-consultation"
              className="group relative inline-flex items-center gap-2 rounded-full border border-navy-900/15 bg-navy-900/5 px-6 py-3.5 text-sm font-semibold text-navy-900 transition-[colors,transform] duration-300 hover:scale-105 hover:border-navy-900/30 hover:bg-navy-900/10"
            >
              <span className="absolute inset-0 -z-10 rounded-full bg-navy-900 opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-10" />
              <Phone className="h-4 w-4 text-emerald2-500" />
              {PHONE}
            </Link>
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="group relative inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(37,211,102,0.45)] transition-transform duration-300 hover:scale-105"
            >
              <span className="absolute inset-0 -z-10 rounded-full bg-[#25D366] opacity-0 blur-lg transition-opacity duration-300 group-hover:opacity-60" />
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp Now
            </a>
          </motion.div>
        </div>

        {/* Right — image */}
        <div className="lg:col-span-5 lg:col-start-8">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2.4rem] bg-gradient-to-br from-gold-500/20 via-transparent to-emerald2-500/20 blur-xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-navy-900/10 shadow-[0_30px_80px_rgba(15,30,60,0.25)]">
              <img
                src={HERO_IMAGE}
                alt="Professional finance and compliance workspace"
                className="h-[420px] w-full object-cover lg:h-[520px]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/30 via-transparent to-transparent" />
            </div>

            <motion.div style={{ y: badgeY }} className="absolute -bottom-6 -left-6 hidden sm:block">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                className="flex items-center gap-3 rounded-2xl border border-navy-900/10 bg-white px-5 py-4 shadow-xl"
                data-testid="hero-floating-badge"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-emerald2-500 text-white">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold text-navy-900">ISO-Level security</p>
                  <p className="text-xs text-slate-500">Your data, encrypted</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Trust marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="relative mt-16 border-y border-navy-900/10 bg-navy-50/60 py-4"
        data-testid="hero-trust-strip"
      >
        <Marquee gradient={false} speed={38} autoFill pauseOnHover>
          {TRUST_STRIP.map((item, i) => (
            <div key={i} className="flex items-center gap-3 px-8">
              <span className={`h-1.5 w-1.5 rounded-full ${i % 2 === 0 ? "bg-gold-500" : "bg-emerald2-500"}`} />
              <span className="text-sm font-semibold uppercase tracking-[0.14em] text-navy-800/80">{item}</span>
            </div>
          ))}
        </Marquee>
      </motion.div>

      {/* Stats strip */}
      <div className="relative mx-auto max-w-7xl px-5 py-12 lg:px-8">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-3 lg:grid-cols-5" data-testid="hero-stats">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`border-l-2 pl-4 ${i % 2 === 0 ? "border-gold-500/50" : "border-emerald2-500/50"}`}
            >
              <div className="text-3xl font-extrabold tracking-tight text-navy-950 lg:text-4xl">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-1 text-sm font-semibold text-navy-800">{s.label}</p>
              <p className="text-xs text-slate-500">{s.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
